import { memo, useMemo, type ReactNode } from 'react'
import * as THREE from 'three'
import type { OrgaoImpactos } from '../../dominio/didatico/quatroImpactos'
import { colidirOrgao } from '../../dominio/didatico/colisaoOrgaos'

type Ponto = [number, number, number]
interface Props {
  deslocamento: number
  foco: OrgaoImpactos | null
  aoSelecionar: (orgao: OrgaoImpactos) => void
  destacar: boolean
}

function Tubo({ pontos, raio = 0.008, cor, opacidade = 1 }: { pontos: Ponto[]; raio?: number; cor: string; opacidade?: number }) {
  const coordenadas = JSON.stringify(pontos)
  const curva = useMemo(() => new THREE.CatmullRomCurve3(pontos.map(p => new THREE.Vector3(...p))), [coordenadas])
  return <mesh><tubeGeometry args={[curva, 40, raio, 8, false]} /><meshStandardMaterial color={cor} roughness={0.65} transparent={opacidade < 1} opacity={opacidade} /></mesh>
}

function Volume({ posicao = [0, 0, 0], escala, cor, brilho = 0, rotacao = [0, 0, 0] }: {
  posicao?: Ponto; escala: Ponto; cor: string; brilho?: number; rotacao?: Ponto
}) {
  return <mesh position={posicao} scale={escala} rotation={rotacao}>
    <sphereGeometry args={[1, 32, 24]} />
    <meshStandardMaterial color={cor} roughness={0.62} emissive={cor} emissiveIntensity={brilho} />
  </mesh>
}

/**
 * Órgão que avança por inércia até a parede (crânio, esterno, costelas, parede abdominal) e,
 * ao encostar, amassa nela em vez de atravessá-la. `frente` é a face anterior do órgão em repouso;
 * `folga`, a distância livre até a parede. Os filhos usam as coordenadas de repouso.
 */
function Contido({ avanco, folga, frente, meia, cx, cy, achatamentoMax, children, ...grupo }: {
  avanco: number; folga: number; frente: number; meia: number; cx: number; cy: number; achatamentoMax?: number
  name?: string; onClick?: (e: { stopPropagation: () => void }) => void; children: ReactNode
}) {
  const c = colidirOrgao(avanco, folga, meia, achatamentoMax), parede = frente + folga
  return <group {...grupo}>
    <group position={[cx, cy, parede]} scale={[c.alargamento, c.alargamento, 1 - c.achatamento]}>
      <group position={[-cx, -cy, -parede]}><group position={[0, 0, c.deslocamento]}>{children}</group></group>
    </group>
  </group>
}

/** Encéfalo em coordenadas do tronco (centro em y = 1,48): bate no crânio e achata no golpe. */
export const EncefaloImpactos3D = memo(function EncefaloImpactos3D({ deslocamento: d, foco, aoSelecionar, destacar }: Props) {
  const brilho = foco === 'encefalo' ? 0.45 : destacar ? 0.16 : 0
  const c = colidirOrgao(d * 0.9, 0.012, 0.117, 0.25)
  return <group name="encefalo" onClick={(e: { stopPropagation: () => void }) => { e.stopPropagation(); aoSelecionar('encefalo') }}>
    <Contido avanco={d * 0.9} folga={0.012} frente={0.121} meia={0.117} achatamentoMax={0.25} cx={0} cy={1.48}>
      <group position={[0, 1.48, 0]}>
      {([-1, 1] as const).map(lado => <group key={lado} position={[lado * 0.051, 0, 0]}>
        <Volume escala={[0.083, 0.103, 0.117]} cor="#f43f5e" brilho={brilho} />
        {Array.from({ length: 12 }, (_, i) => {
          const phi = 0.25 + i * Math.PI / 12
          const pontos: Ponto[] = Array.from({ length: 30 }, (_, j) => {
            const theta = 0.15 + j / 29 * (Math.PI - 0.3)
            const ondulacao = 0.008 * Math.sin(theta * 14 + i * 1.4)
            return [(0.086 + ondulacao) * Math.sin(theta) * Math.cos(phi), 0.106 * Math.cos(theta), (0.121 + ondulacao) * Math.sin(theta) * Math.sin(phi)]
          })
          return <Tubo key={i} pontos={pontos} raio={0.0035} cor="#f9a8b4" />
        })}
      </group>)}
      <Volume posicao={[0, -0.084, -0.077]} escala={[0.089, 0.044, 0.061]} cor="#c65b7e" />
      {Array.from({ length: 7 }, (_, i) => <Tubo key={i} pontos={Array.from({ length: 20 }, (_, j): Ponto => {
        const a = j / 19 * Math.PI
        return [0.083 * Math.cos(a), -0.11 + i * 0.009, -0.077 + 0.058 * Math.sin(a)]
      })} raio={0.003} cor="#f6a2b7" />)}
      <Volume posicao={[0, -0.125, -0.045]} escala={[0.018, 0.055, 0.022]} cor="#e6a1a0" />
      </group>
    </Contido>
    {/* Golpe na frente e, no recuo, contragolpe atrás: aparecem quando há contato com o crânio. */}
    {c.excesso > 0 && <>
      <mesh position={[0, 1.48, 0.134]}><ringGeometry args={[0.03, 0.055, 24]} /><meshBasicMaterial color="#fbbf24" transparent opacity={Math.min(0.9, c.achatamento * 4)} side={THREE.DoubleSide} /></mesh>
      <mesh position={[0, 1.48, -0.128]} rotation={[0, Math.PI, 0]}><ringGeometry args={[0.03, 0.055, 24]} /><meshBasicMaterial color="#ef4444" transparent opacity={Math.min(0.9, c.achatamento * 3)} side={THREE.DoubleSide} /></mesh>
    </>}
  </group>
})

// Coração: transformação de repouso -> posição final, para ancorar a aorta descendente na coluna.
const CORACAO = { x: 0.03, y: 0.955, z: 0.055, giro: -0.22 }
const pontoDoCoracao = ([x, y, z]: Ponto, repouso: boolean, c: ReturnType<typeof colidirOrgao>): Ponto => {
  const cos = Math.cos(CORACAO.giro), sin = Math.sin(CORACAO.giro)
  const px = CORACAO.x + x * cos - y * sin, py = CORACAO.y + x * sin + y * cos, pz = CORACAO.z + z
  if (repouso) return [px, py, pz]
  const parede = 0.111 + 0.009, k = c.alargamento
  return [CORACAO.x + (px - CORACAO.x) * k, CORACAO.y + (py - CORACAO.y) * k, parede + (pz + c.deslocamento - parede) * (1 - c.achatamento)]
}

/** Formas construídas em coordenadas anatômicas: +Z anterior; -X direito do paciente. */
export const AnatomiaImpactos3D = memo(function AnatomiaImpactos3D({ deslocamento: d, foco, aoSelecionar, destacar }: Props) {
  const brilho = (id: OrgaoImpactos) => foco === id ? 0.45 : destacar ? 0.16 : 0
  const grupo = (id: OrgaoImpactos) => ({
    name: id,
    onClick: (e: { stopPropagation: () => void }) => { e.stopPropagation(); aoSelecionar(id) },
  })
  const avancoRim = d * 0.35, avancoBaco = d * 0.48
  const coracao = colidirOrgao(d * 0.7, 0.009, 0.05, 0.4)
  const istmoFixo = pontoDoCoracao([0.078, -0.21, -0.12], true, coracao)
  return <group>
    <Contido {...grupo('pulmoes')} avanco={d * 0.55} folga={0.01} frente={0.097} meia={0.09} cx={0} cy={0.99}>
      <group position={[0, 0.99, 0]}>
      {/* Direito: três lobos; esquerdo: dois lobos e espaço para o coração. */}
      <Volume posicao={[-0.125, 0.065, -0.016]} escala={[0.076, 0.136, 0.082]} cor="#38bdf8" brilho={brilho('pulmoes')} />
      <Volume posicao={[-0.139, -0.033, 0.04]} escala={[0.083, 0.066, 0.057]} cor="#56b7d6" brilho={brilho('pulmoes')} />
      <Volume posicao={[-0.137, -0.101, -0.02]} escala={[0.088, 0.095, 0.089]} cor="#2b95b6" brilho={brilho('pulmoes')} />
      <Volume posicao={[0.133, 0.064, -0.035]} escala={[0.072, 0.139, 0.078]} cor="#38bdf8" brilho={brilho('pulmoes')} />
      <Volume posicao={[0.145, -0.099, -0.044]} escala={[0.077, 0.10, 0.076]} cor="#2b95b6" brilho={brilho('pulmoes')} />
      <Tubo pontos={[[0, 0.29, 0.02], [0, 0.14, 0.015], [0, 0.055, 0]]} raio={0.016} cor="#d6e6ed" />
      {Array.from({ length: 9 }, (_, i) => <mesh key={i} position={[0, 0.1 + i * 0.021, 0.018]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.017, 0.0028, 6, 20]} /><meshStandardMaterial color="#96b6c9" />
      </mesh>)}
      {[-1, 1].map(lado => <group key={lado}>
        <Tubo pontos={[[0, 0.059, 0.006], [lado * 0.055, 0.013, 0.035], [lado * 0.126, -0.08, 0.067]]} raio={0.012} cor="#e6edf1" />
        {[0, 1, 2, 3].map(i => <Tubo key={i} pontos={[[lado * 0.046, 0.023, 0.035], [lado * 0.095, 0.1 - i * 0.067, 0.074], [lado * 0.165, 0.13 - i * 0.083, 0.083]]} raio={0.005} cor="#e6edf1" />)}
      </group>)}
      </group>
    </Contido>

    <Contido {...grupo('coracao')} avanco={d * 0.7} folga={0.009} frente={0.111} meia={0.05} achatamentoMax={0.4} cx={CORACAO.x} cy={CORACAO.y}>
      <group position={[CORACAO.x, CORACAO.y, CORACAO.z]} rotation={[0, 0, CORACAO.giro]}>
      <Volume posicao={[-0.026, 0.034, 0]} escala={[0.04, 0.04, 0.041]} cor="#d75c65" brilho={brilho('coracao')} />
      <Volume posicao={[0.032, 0.037, -0.018]} escala={[0.041, 0.039, 0.041]} cor="#c54657" brilho={brilho('coracao')} />
      <Volume posicao={[-0.027, -0.025, 0.017]} escala={[0.049, 0.068, 0.039]} cor="#ef4444" brilho={brilho('coracao')} />
      <Volume posicao={[0.027, -0.034, 0.008]} escala={[0.047, 0.079, 0.04]} cor="#cf3344" brilho={brilho('coracao')} />
      <Tubo pontos={[[0.015, 0.016, 0.064], [0.002, -0.024, 0.063], [0.025, -0.086, 0.024]]} raio={0.004} cor="#ffd0a0" />
      <Tubo pontos={[[0.003, 0.001, 0.062], [-0.027, -0.006, 0.057], [-0.058, -0.046, 0.026]]} raio={0.003} cor="#ffd0a0" />
      <Tubo pontos={[[0, 0.041, 0], [0, 0.12, 0], [0.05, 0.163, -0.039], [0.084, 0.122, -0.07]]} raio={0.015} cor="#dc2626" />
      {[0, 1, 2].map(i => <Tubo key={i} pontos={[[0.024 + i * 0.018, 0.145, -0.02 - i * 0.019], [0.015 + i * 0.027, 0.21, -0.045]]} raio={0.006} cor="#dc2626" />)}
      <Tubo pontos={[[-0.028, 0.025, -0.021], [-0.043, 0.12, -0.032], [-0.033, 0.195, -0.04]]} raio={0.014} cor="#627cdb" />
      <Tubo pontos={[[0, 0.06, 0.029], [0.006, 0.107, 0.046], [-0.092, 0.11, 0.034]]} raio={0.013} cor="#6892db" />
      {destacar && <mesh position={[0.077, 0.113, -0.07]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.028, 0.003, 6, 24]} /><meshBasicMaterial color="#fbbf24" />
      </mesh>}
      </group>
    </Contido>
    {/* Aorta descendente presa à coluna: o arco avança com o coração e o istmo é tracionado. */}
    <Tubo pontos={[pontoDoCoracao([0.084, 0.122, -0.07], false, coracao), istmoFixo]} raio={0.015} cor="#dc2626" />

    {/* Diafragma: limite visual entre tórax e abdome. */}
    <mesh position={[0, 0.79, 0]} scale={[0.22, 0.045, 0.125]}>
      <sphereGeometry args={[1, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
      <meshStandardMaterial color="#bd8f86" transparent opacity={0.23} side={THREE.DoubleSide} depthWrite={false} />
    </mesh>
    <Contido {...grupo('figado')} avanco={d * 0.58} folga={0.005} frente={0.11} meia={0.09} cx={-0.072} cy={0.754}>
      <group position={[-0.072, 0.754, 0]}>
      <Volume posicao={[-0.043, 0, 0.015]} escala={[0.12, 0.075, 0.095]} cor="#ea580c" brilho={brilho('figado')} rotacao={[0, 0, -0.13]} />
      <Volume posicao={[0.073, 0.013, 0.027]} escala={[0.087, 0.047, 0.067]} cor="#cc591f" brilho={brilho('figado')} />
      <Tubo pontos={[[-0.035, 0.056, 0.075], [0.007, 0.052, 0.089], [0.027, -0.043, 0.075]]} raio={0.0025} cor="#f7ad75" />
      <Volume posicao={[-0.003, -0.061, 0.051]} escala={[0.016, 0.03, 0.016]} cor="#709963" />
      </group>
    </Contido>
    <Contido {...grupo('baco')} avanco={avancoBaco} folga={0.012} frente={-0.001} meia={0.042} cx={0.181} cy={0.748}>
      <group position={[0.181, 0.748, -0.043]}>
      <Volume escala={[0.033, 0.066, 0.042]} rotacao={[0, 0, -0.33]} cor="#b45309" brilho={brilho('baco')} />
      <Tubo pontos={[[0, 0.028, 0.041], [-0.011, 0, 0.045], [-0.014, -0.033, 0.03]]} raio={0.003} cor="#f3a882" />
      {/* Pedículo fixo: estira quando o baco avança. */}
      <Tubo pontos={[[-0.011, 0, 0.045], [-0.03, -0.006, 0.03 - avancoBaco * 0.5], [-0.051, -0.013, 0.013 - avancoBaco]]} raio={0.004} cor="#c0392b" />
      </group>
    </Contido>
    <group {...grupo('rins')}>
      {[-1, 1].map(lado => <group key={lado} position={[lado * 0.112, lado === -1 ? 0.648 : 0.67, -0.076 + avancoRim]} rotation={[0, 0, lado * 0.12]}>
        <Volume posicao={[lado * 0.006, 0.022, 0]} escala={[0.029, 0.04, 0.025]} cor="#a855f7" brilho={brilho('rins')} />
        <Volume posicao={[lado * 0.009, -0.022, 0]} escala={[0.031, 0.038, 0.026]} cor="#9856bc" brilho={brilho('rins')} />
        <Tubo pontos={[[-lado * 0.019, 0, 0.016], [-lado * 0.054, 0.01, 0.008 - avancoRim]]} raio={0.005} cor="#dc6161" />
        <Tubo pontos={[[-lado * 0.018, -0.008, 0.021], [-lado * 0.031, -0.064, 0.009 - avancoRim * 0.5], [-lado * 0.05, -0.175, 0.037 - avancoRim]]} raio={0.0035} cor="#e3ca94" />
      </group>)}
    </group>
    <Contido {...grupo('digestivo')} avanco={d * 0.44} folga={0} frente={0.096} meia={0.058} cx={0} cy={0.64}>
      <Volume posicao={[0.088, 0.694, 0.027]} escala={[0.052, 0.073, 0.045]} cor="#e9ad8c" rotacao={[0, 0, -0.4]} brilho={brilho('digestivo')} />
      <Tubo pontos={[[0.046, 0.824, -0.012], [0.074, 0.751, 0.005], [0.094, 0.706, 0.016]]} raio={0.013} cor="#e7b8a5" />
      <Tubo pontos={Array.from({ length: 125 }, (_, i): Ponto => {
        const t = i / 124
        return [0.091 * Math.sin(t * Math.PI * 14), 0.65 - t * 0.165, 0.071 + 0.012 * Math.cos(t * Math.PI * 14)]
      })} raio={0.013} cor="#e9ad8c" />
      <Tubo pontos={[[-0.145, 0.495, 0.042], [-0.155, 0.616, 0.035], [-0.129, 0.674, 0.035], [0, 0.648, 0.065], [0.142, 0.671, 0.025], [0.158, 0.548, 0.037], [0.09, 0.482, 0.051], [0.007, 0.466, 0.017]]} raio={0.022} cor="#d99282" />
      {Array.from({ length: 8 }, (_, i) => <Volume key={i} posicao={[-0.123 + i * 0.035, 0.652, 0.047]} escala={[0.019, 0.023, 0.024]} cor="#e5a48c" />)}
    </Contido>
  </group>
})

export function CranioImpactos3D() {
  return <mesh position={[0, 1.48, 0]} scale={[0.15, 0.18, 0.15]}>
    <sphereGeometry args={[1, 32, 24]} /><meshStandardMaterial color="#d4edff" transparent opacity={0.12} depthWrite={false} wireframe />
  </mesh>
}

export function EsqueletoImpactos3D() {
  return <group>
    {Array.from({ length: 20 }, (_, i) => <Volume key={i} posicao={[0, 0.51 + i * 0.04, -0.102]} escala={[0.025, 0.015, 0.019]} cor="#d2dce6" />)}
    {[-1, 1].map(lado => <group key={lado}>
      {Array.from({ length: 10 }, (_, i) => <Tubo key={i} pontos={[
        [0, 1.17 - i * 0.031, -0.092], [lado * (0.12 + Math.sin(i / 9 * Math.PI) * 0.095), 1.148 - i * 0.032, -0.01],
        [lado * (0.13 + Math.sin(i / 9 * Math.PI) * 0.08), 1.1 - i * 0.033, 0.084], [lado * 0.015, 1.08 - i * 0.029, 0.123],
      ]} raio={0.0045} cor="#bdd1e0" opacidade={0.3} />)}
    </group>)}
    <Volume posicao={[0, 1.035, 0.12]} escala={[0.012, 0.121, 0.005]} cor="#c8d9e3" />
  </group>
}
